import {
  Box,
  Tabs,
  TabList,
  Tab,
  Heading,
  Text,
  Divider,
  Flex,
  Link,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  SettingsBreadcrumb,
  SettingsFacilitiesBreadcrumb,
} from './SettingsBreadcrumbs';
import { useCreditFacilityOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { colors } from '~/packages/common-frontend/colors';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';

// Types
type SettingsFacilityBorrowingBaseTab =
  | 'loantape'
  | 'configuration'
  | 'sandbox';
type SettingsFacilityBorrowingBaseTabType = {
  key: SettingsFacilityBorrowingBaseTab;
  label: string;
  route: string;
};

// Exports
type SettingsFacilityBorrowingBaseTemplateProps = {
  creditFacilityId: string;
  selectedTab: SettingsFacilityBorrowingBaseTab;
  children: React.ReactNode;
};
export const SettingsFacilityBorrowingBaseTemplate = ({
  creditFacilityId,
  selectedTab,
  children,
}: SettingsFacilityBorrowingBaseTemplateProps) => {
  const flags = useFeatureFlags();
  const navigate = useNavigate();
  const creditFacility = useCreditFacilityOrThrow(creditFacilityId);

  // can configure feature flags for specific tabs as needed in the future
  // to conditionally render tabs
  const tabs: SettingsFacilityBorrowingBaseTabType[] = [
    {
      key: 'configuration',
      label: 'Configuration',
      route: `/settings/credit-facilities/${creditFacilityId}/borrowing-base/configuration`,
    },
  ];

  if (flags.enableLenderBaseFieldEdits) {
    // add settingsFacilityBorrowingBaseTabOptions.loantape at the top of tabs.
    tabs.unshift({
      key: 'loantape',
      label: 'Loan Tape',
      route: `/settings/credit-facilities/${creditFacilityId}/borrowing-base/loantape`,
    });
  }

  const breadcrumbs = [
    SettingsBreadcrumb,
    SettingsFacilitiesBreadcrumb,
    { label: creditFacility.name },
    { label: 'Borrowing Base' },
  ];

  return (
    <Box p="40px">
      <Flex flexWrap={['wrap', 'wrap', 'wrap', 'nowrap']}>
        <Flex direction="column" flexGrow={1} flexShrink={0} maxWidth="100%">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          <Heading as="h1" mt="16px" mb="16px" fontSize="36px" fontWeight={600}>
            Borrowing Base
          </Heading>
          <Text fontSize="16px" mb="16px" color={colors.black[70]}>
            Configure the borrowing base mechanics for{' '}
            <Text as="span" fontSize="16px" mb="16px" fontWeight={500}>
              {creditFacility.name}.
            </Text>
          </Text>
        </Flex>
        <Flex direction="column">
          <Link
            color={colors.gray[500]}
            fontSize="0.75rem"
            as={RouterLink}
            to="./../../portfolio-covenants/configuration"
          >
            Go to Portfolio Covenant Settings
          </Link>
        </Flex>
      </Flex>
      <Divider />

      <Tabs
        variant="line"
        index={tabs.findIndex(({ key }) => key === selectedTab)}
        colorScheme="blue"
        background="white"
      >
        <TabList>
          {tabs.map(({ route, label, key }) => {
            return (
              <Tab
                key={key}
                onClick={() => {
                  navigate(route);
                }}
                fontSize="sm"
              >
                {label}
              </Tab>
            );
          })}
        </TabList>
      </Tabs>
      <Box mt="32px">{children}</Box>
    </Box>
  );
};
